import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarTimes } from '@fortawesome/free-solid-svg-icons';
import { Row, Col, Progress } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import {
    createDataSource,
    createGridOptions,
    DataGrid,
    DataGridRefreshButton,
    refreshDataGrid,
    LinkCellRenderer,
    TextFilterDefaults,
} from '../common/dataGrid/DataGrid';

import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';

import { Can } from '../Can';
import CommonContext, { ApiRoutes, agGridConstants } from '../Common';
import {
    FlexRow,
    FlexStartRow,
    FlexEndRow,
    PageHeading,
    SmallButton,
    onReactSelectChanged,
    onFieldChange,
    PageWrap,
    toasty,
} from '../common/forms/FormElements';
import CertificationForm from './CertificationForm';
import { util } from '../Util';
import { handleFormSaveError } from '../common/forms/ValidationError';

class CertificationIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            loading: true,
            selectedCertification: null,
            showCertificationForm: false,
            saving: false,
            errors: null,
        };

        this.onChange = onFieldChange.bind(this);
        this.onSelectChange = onReactSelectChanged.bind(this);
        this.handleSaveError = this.handleSaveError.bind(this);
        this.refreshGrid = this.refreshGrid.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    async populateState() {
        const gridOptions = createGridOptions(this);

        gridOptions.components = {
            nameRenderer: LinkCellRenderer,
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
        };

        const yesNo = [
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
        ];

        const yesNoFilterParams = {
            suppressFilterButton: true,
            options: yesNo,
            optionsLabel: 'label',
            optionsValue: 'value',
            initialFilterValue: null,
        };

        gridOptions.columnDefs = [
            {
                colId: 'Name',
                sortable: true,
                headerName: 'Name',
                field: 'name',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: TextFilterDefaults,
                sort: { direction: 'asc', priority: 0 },
                cellRenderer: 'nameRenderer',
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
                cellRendererParams: {
                    clicked: this.onEditCertification,
                    nameField: 'name',
                    idField: 'id',
                    title: 'View this Certification',
                },
            },
            {
                colId: 'Abbreviation',
                sortable: true,
                headerName: 'Abbreviation',
                field: 'abbreviation',
                filter: agGridConstants.columnFilterTypes.text,
                floatingFilter: true,
                filterParams: TextFilterDefaults,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'willExpire',
                sortable: true,
                headerName: 'Expiration',
                field: 'willExpire',
                cellRenderer: this.booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
            {
                colId: 'IsActive',
                sortable: true,
                headerName: 'Active',
                field: 'isActive',
                cellRenderer: this.booleanTranslator,
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: yesNoFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: yesNoFilterParams,
            },
        ];

        const dataSource = createDataSource(
            ApiRoutes.certification.search(),
            gridOptions
        );

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    }

    booleanTranslator = (paramValue) => {
        const value = Boolean(paramValue && paramValue.value);

        return value ? 'Yes' : 'No';
    };


    onAddCertification = () => {
        this.setState({
            selectedCertification: {},
            showCertificationForm: true,
        });
    };

    onEditCertification = async (certificationId) => {
        const selectedCertification = await util.fetch.js(
            ApiRoutes.certification.byId(certificationId)
        );

        this.setState({
            selectedCertification,
            showCertificationForm: true,
        });
    };

    onCertificationSaveCallback = async () => {
        await this.setState({ saving: true });
        const { selectedCertification } = this.state;

        const isEdit = !!(selectedCertification.id && selectedCertification.id > 0);
        const saveUrl = isEdit
            ? ApiRoutes.certification.update(selectedCertification.id)
            : ApiRoutes.certification.create();

        const response = await util.fetch
            .andGetResponse(
                util.fetch.types.post,
                saveUrl,
                selectedCertification,
                'Error Saving Certification',
                () => this.setState({ saving: false })
            )
            .catch(this.handleSaveError);

        if (response && response > 0) {
            this.notifySuccess();

            this.setState({
                selectedCertification: null,
                showCertificationForm: false,
                saving: false,
            });

            this.state.gridApi.setDatasource(this.state.dataSource);
        }
    };

    onCertificationFormClosed = () => {
        this.setState({
            selectedCertification: null,
            showCertificationForm: false,
        });
    };

    onChange = onFieldChange;
    onSelectChange = onReactSelectChanged;

    handleSaveError = (err) => {
        console.debug(err);
        handleFormSaveError(this, err);
    };

    notifyError = (message) =>
        toasty.error(
            'Save Unsuccessful',
            `There was a server error saving this certification (${message}).`
        );

    notifySuccess = () =>
        toasty.success(
            'Certification Saved',
            'Certification saved successfully.'
        );

    refreshGrid = () => refreshDataGrid;

    render() {
        if (this.state.loading) {
            return <Progress />;
        }

        const {
            rowData,
            gridOptions,
            showCertificationForm,
            selectedCertification,
        } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon icon={faCalendarTimes} className="mr-2 text-muted" />
                    <span>Certifications</span>
                </PageHeading>

                <Row>
                    <Col>
                        <FlexRow>
                            <FlexStartRow>
                                <DataGridRefreshButton
                                    gridApi={this.state.gridApi}
                                    dataSource={this.state.dataSource}
                                />
                            </FlexStartRow>
                            <FlexEndRow>
                                <Can I="create" a="certification">
                                    <SmallButton
                                        type="button"
                                        disabled={showCertificationForm}
                                        onClick={this.onAddCertification}
                                    >
                                        <i className="fa fa-plus-circle fa-md mr-2" />
                                                Add Certification
                                    </SmallButton>
                                </Can>
                            </FlexEndRow>
                        </FlexRow>
                    </Col>
                </Row>

                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                />

                <CertificationForm
                    selectedCertification={selectedCertification}
                    onChange={this.onChange}
                    onSelectChange={this.onSelectChange}
                    onClose={this.onCertificationFormClosed}
                    show={showCertificationForm}
                    onSaveCallback={this.onCertificationSaveCallback}
                />
            </PageWrap>
        );
    }
}

export default withRouter(CertificationIndex);
